import { useStripe, useElements, PaymentElement, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import CommonService from 'Services/CommonService';
import "./StripePayment.css";
import { Dispatch, useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { Loader } from 'Components/Loader/Loader';
import { getTokenDetail } from '../../helpers/helper';
import { OrderStatus } from 'Components/constants/PandaConstants';

type Props = {
    restaurantId?: string;
    callBack: (paymentKey: string, isToast: boolean) => void;
    amount: any;
    loginId?: any;
    setPaymentStart?: Dispatch<{ paymentProcess: boolean, orderCreation: boolean }>;
    isPaymentStart: { paymentProcess: boolean, orderCreation: boolean };
    setIsPaymentOpen?: (isOpen: boolean) => void;
    setStripePromise?: any;
    menuName: string;
    isSetupIntent: boolean;
    isExistingCard: boolean;
    clientSecret: string;
    createPaymentLogs: (comments: string,statusId:number,paymentmethodId?: string, cardId?: string, transactionId?: string) => void
}

const StripePayment = ({
    callBack,
    amount,
    isPaymentStart,
    menuName,
    isSetupIntent,
    isExistingCard,
    clientSecret,
    createPaymentLogs
}: Props) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isPaymentErr, setIsPaymentErr] = useState("");
    const [paymentRequest, setPaymentRequest] = useState<any>(null);
    const [isModelOpen, setIsModelOpen] = useState(false);
    const [loader, setLoader] = useState(false);
    const [stripeLoaded, setStripeLoaded] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }
        setLoader(true);
        if (isSetupIntent) {
            createPaymentLogs("User clicked Pay button by entering the card details!. Confirm setup method is initiated",OrderStatus.PaymentInProgress);
            const { error, setupIntent } = await stripe.confirmSetup({
                elements,
                redirect: "if_required",
            });
            if (error) {
                setLoader(false);
                setIsModelOpen(true);
                createPaymentLogs(`Failed to confirm the setup intent: ${error.message}`,OrderStatus.PaymentFailed);
                setIsPaymentErr(error.message as string);
                return;
            }
            if (setupIntent) {
                createPaymentLogs("Setup Intent is confirmed",OrderStatus.PaymentInitidated);
                await saveStripeCardData(setupIntent.id);
            }
        } else {
            createPaymentLogs("User clicked Pay button by entering the card details!. Confirm payment method is initiated",OrderStatus.PaymentInitidated);
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: "if_required",
            });
            if (error) {
                setLoader(false);
                setIsModelOpen(true);
                createPaymentLogs(`Failed to confirm the payment intent: ${error.message}`,OrderStatus.PaymentFailed);
                setIsPaymentErr(error.message as string);
                return;
            }
            if (paymentIntent) {
                createPaymentLogs("Payment Intent has been confirmed and the payment has been completed",OrderStatus.PaymentCompleted,"","",paymentIntent.id);
                onStripeClick(paymentIntent.id);
            }
        }
    };

    const saveStripeCardData = (setupIntentId: string) => {
        const token = getTokenDetail() as any;
        const userId = token.LoginId;

        CommonService.postWithDoubleParam('Payment', "CreateStripeDetails", setupIntentId, userId)
            .then(res => {
                if (res.status === 200) {
                    if (res.data != 0) {
                        createPaymentLogs("Card save method triggered!This card is already saved",OrderStatus.PaymentInProgress);
                        callBack(setupIntentId, true);
                    } else {
                        createPaymentLogs("Card save method triggered and saved successfully",OrderStatus.PaymentInProgress);
                        callBack(setupIntentId, false);
                    }
                }
            })
            .catch(error => {
                createPaymentLogs(`Error saving card data:${error}`,OrderStatus.OrderInitiated);
                console.error("Error saving card data:", error);
            });
    }

    const onStripeClick = (paymentKey: string) => {
        CommonService.postWithQueryParam("Payment", "GetPaymentResponse", "paymentId", paymentKey)
            .then(async res => {
                if (res.status === 200) {
                    if (res.data === "succeeded") {
                        await createPaymentLogs("Received payment success response",OrderStatus.PaymentCompleted,"7CF167E95CFEBEA1E7C39382001017A5","",paymentKey);
                        callBack(paymentKey, false);
                    } else {
                        setIsModelOpen(true);
                        createPaymentLogs("Payment failed",OrderStatus.PaymentFailed);
                        setIsPaymentErr("OOPS, Your Payment Failed, Try Again!");
                    }
                }
            })
            .catch(error => {
                setLoader(false);
                setIsModelOpen(true);
                setIsPaymentErr("An error occurred while processing payment. Please try again.");
                console.error("Payment processing error:", error);
            });
    }

    const onClose = () => {
        setIsModelOpen(false);
    }

    const onRetryClicked = () => {
        createPaymentLogs("User has clicked retry button!",OrderStatus.PaymentFailed);
        setIsModelOpen(false);
        window.location.reload();
    }

    const initializePaymentRequest = () => {
        if (!stripe) return;

        const paymentRequestOptions = stripe.paymentRequest({
            currency: "usd",
            country: "US",
            requestPayerEmail: true,
            requestPayerName: true,
            total: {
                amount: Number((amount * 100).toFixed(0)) as number,
                label: menuName
            }
        });

        paymentRequestOptions.canMakePayment()
            .then(result => {
                if (result) {
                    createPaymentLogs("Stripe Payment Method Initiated!",OrderStatus.PaymentInProgress);
                    setPaymentRequest(paymentRequestOptions);
                }
            })
            .catch(error => {
                createPaymentLogs(`Error checking payment method availability ${error}`,OrderStatus.PaymentFailed);
                console.error("Error checking payment method availability:", error);
            });
    }

    const payUsingExistingCard = async () => {
        if (!isExistingCard || !clientSecret) return;

        // Make sure we don't try to pay if we've already set the loader
        if (loader) return;

        setLoader(true);

        if (!stripe) {
            setLoader(false);
            setIsModelOpen(true);
            setIsPaymentErr("Stripe.js has not loaded yet. Please try again.");
            return;
        }

        try {
            createPaymentLogs("Stripe Payment Initiated with existing card!",OrderStatus.PaymentInProgress);
            const { error, paymentIntent } = await stripe.confirmPayment({
                clientSecret,
                redirect: "if_required",
            });

            if (error) {
                createPaymentLogs(`Payment has not been completed due to error: ${error.message}`,OrderStatus.PaymentFailed);
                setLoader(false);
                setIsModelOpen(true);
                setIsPaymentErr(error.message as string);
                return;
            }

            if (paymentIntent) {
                createPaymentLogs(`Payment completed with the transaction id: ${paymentIntent.id}`,OrderStatus.PaymentCompleted,"","",paymentIntent.id);
                onStripeClick(paymentIntent.id);
            }
        } catch (error) {
            setLoader(false);
            setIsModelOpen(true);
            setIsPaymentErr("An unexpected error occurred. Please try again.");
            console.error("Payment confirmation error:", error);
        }
    };

    useEffect(() => {
        if (stripe) {
            setStripeLoaded(true);
        }
    }, [stripe]);

    useEffect(() => {
        if (stripeLoaded) {
            initializePaymentRequest();
        }
    }, [stripeLoaded]);

    useEffect(() => {
        if (stripeLoaded && isExistingCard && clientSecret && !loader) {
            const timer = setTimeout(() => {
                payUsingExistingCard();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [stripeLoaded, clientSecret, isExistingCard]);

    return (
        <>
            <form onSubmit={handleSubmit}>
                <div className="p-2 row">
                    <div className="col-12" style={{ display: "grid", alignItems: "center" }}>
                        {paymentRequest && (
                            <div className='mb-3'>
                                <span className='line-or-line'></span>
                                <PaymentRequestButtonElement options={{ paymentRequest }} />
                            </div>
                        )}
                        <PaymentElement
                            options={{
                                paymentMethodOrder: ['apple_pay', 'google_pay', 'card', 'klarna']
                            }}
                            className='custom-StripeElement'
                        />
                    </div>
                    <Loader open={loader} />
                    <div className="mt-2 text-center col-12">
                        <Button
                            className='btn btn-success payment-btn'
                            disabled={!stripe || isPaymentStart.paymentProcess || loader}
                            type="submit"
                        >
                            Pay Now
                        </Button>
                    </div>
                </div>
            </form>
            <Modal isOpen={isModelOpen} toggle={onClose} backdrop="static" >
                <ModalBody>
                    {isPaymentErr && (
                        <span style={{
                            color: "red",
                            fontSize: "15px",
                            fontWeight: "500",
                            textAlign: "left",
                            margin: "6px"
                        }}>
                            <i className="fas fa-arrows-rotate"></i> {isPaymentErr}
                        </span>
                    )}
                </ModalBody>
                <ModalFooter>
                    {/* <Button
                        style={{
                            width: '80px',
                            height: '37px',
                            borderRadius: '5px',
                            border: '1px',
                            padding: '1px 11px 1px 11px'
                        }}
                        type="button"
                        onClick={onClose}
                    >
                        No
                    </Button> */}
                    <Button
                        style={{
                            backgroundColor: '#E75605',
                            width: '80px',
                            height: '37px',
                            borderRadius: '5px',
                            border: '1px',
                            padding: '1px 11px 1px 11px'
                        }}
                        type="button"
                        onClick={onRetryClicked}
                    >
                        Retry
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default StripePayment;